import { csrfHeaders } from "../auth/session-api.js";

/**
 * The tenant people directory (PB-138) — the shared picker source for owner
 * assignment, mentions, recipients and the KPI owner field.
 *
 * Reachable by any ACTIVE MEMBER, unlike `admin-users-api`, which is
 * tenant-admin only and searches Microsoft Entra rather than the tenant's own
 * directory. Search and resolve are POSTs so a name or e-mail prefix never
 * lands in a URL or an access log; both therefore carry the CSRF header.
 */

export interface DirectoryPerson {
  readonly userId: string;
  readonly displayName: string;
  readonly primaryEmail: string | null;
}

export interface DirectoryPeoplePage {
  readonly items: readonly DirectoryPerson[];
  readonly nextCursor: string | null;
  readonly totalCount: number;
}

export interface DirectoryGroup {
  readonly groupId: string;
  readonly displayName: string;
}

export interface DirectoryGroupPage {
  readonly items: readonly DirectoryGroup[];
  readonly nextCursor: string | null;
}

export class DirectoryApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly retryAfterSeconds: number | null,
  ) {
    super(message);
    this.name = "DirectoryApiError";
  }
}

export async function searchDirectoryPeople(input: {
  query: string;
  groupId?: string | null;
  cursor?: string;
  signal?: AbortSignal;
}): Promise<DirectoryPeoplePage> {
  return (await request(
    "/api/auth/directory/search",
    {
      query: input.query,
      ...(input.groupId ? { groupId: input.groupId } : {}),
      ...(input.cursor ? { cursor: input.cursor } : {}),
    },
    input.signal,
  )) as DirectoryPeoplePage;
}

/**
 * Batch id → display name. Bounded at 50 by the server, so callers holding more
 * ids must chunk; `resolveDirectoryNames` does that for them.
 */
export async function resolveDirectoryIdentities(
  userIds: readonly string[],
  signal?: AbortSignal,
): Promise<readonly { userId: string; displayName: string }[]> {
  if (userIds.length === 0) return [];
  return (await request(
    "/api/auth/directory/resolve",
    { userIds: [...new Set(userIds)] },
    signal,
  )) as readonly { userId: string; displayName: string }[];
}

/**
 * Batch id → identity with status, for chips that render a STORED person
 * (`includeFormer`): former members of this tenant come back `inactive`
 * instead of being omitted. Same 50-id bound as `resolveDirectoryIdentities`.
 */
export async function resolveDirectoryPeople(
  userIds: readonly string[],
  signal?: AbortSignal,
): Promise<
  readonly {
    userId: string;
    displayName: string;
    status: "active" | "inactive";
  }[]
> {
  if (userIds.length === 0) return [];
  return (await request(
    "/api/auth/directory/resolve",
    { userIds: [...new Set(userIds)], includeFormer: true },
    signal,
  )) as readonly {
    userId: string;
    displayName: string;
    status: "active" | "inactive";
  }[];
}

const RESOLVE_BATCH = 50;

/**
 * A cancelled walk must FAIL, not hand back what it happened to collect.
 *
 * Both aggregations below are consumed through TanStack Query, which treats a
 * resolved value as a complete result and caches it — so returning a half-walked
 * list on cancellation stored a truncated group list, or a partial name map, and
 * served it as the whole truth for the rest of the staleTime. Rejecting keeps it
 * out of the cache; the group filter's `retry: false` / `data ?? []` then
 * degrades to no filter, which is its designed failure mode.
 */
function abortError(): DOMException {
  return new DOMException("Aborted", "AbortError");
}

/**
 * Resolve any number of ids to a name map, in server-sized batches.
 *
 * Unresolvable ids are simply absent from the result — a person who left the
 * tenant has no name to disclose — so callers must keep their own fallback.
 */
export async function resolveDirectoryNames(
  userIds: readonly string[],
  signal?: AbortSignal,
): Promise<Record<string, string>> {
  const unique = [...new Set(userIds)];
  const names: Record<string, string> = {};
  for (let index = 0; index < unique.length; index += RESOLVE_BATCH) {
    if (signal?.aborted) throw abortError();
    const batch = unique.slice(index, index + RESOLVE_BATCH);
    const identities = await resolveDirectoryIdentities(batch, signal);
    // Checked after the await as well. The pre-await check cannot see a
    // cancellation that lands while a request is already in flight: on the LAST
    // batch there is no next iteration to notice it, so the walk resolved and
    // handed a value back to a consumer that had already cancelled — which
    // TanStack Query then caches. Whether that value is complete is not the
    // point; it is a result for a request nobody is waiting for any more.
    if (signal?.aborted) throw abortError();
    for (const identity of identities) {
      names[identity.userId] = identity.displayName;
    }
  }
  return names;
}

export async function listDirectoryGroups(
  cursor?: string,
  signal?: AbortSignal,
): Promise<DirectoryGroupPage> {
  const url = cursor
    ? `/api/auth/directory/groups?cursor=${encodeURIComponent(cursor)}`
    : "/api/auth/directory/groups";
  const response = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/json" },
    ...(signal ? { signal } : {}),
  });
  return (await parse(response)) as DirectoryGroupPage;
}

/**
 * Every active group, paged to exhaustion but terminating, for the picker filter.
 *
 * An EMPTY array is the ordinary answer for a tenant without
 * `GroupMember.Read.All` and without SCIM groups. Callers must render no filter
 * at all on an empty result rather than an empty control.
 */
export async function listAllDirectoryGroups(
  signal?: AbortSignal,
): Promise<readonly DirectoryGroup[]> {
  const groups: DirectoryGroup[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;

  // Pages until the server stops offering a cursor, which is the only ordinary
  // way out. A page count is not used as a bound because it would silently
  // truncate a large tenant's groups and the picker would quietly offer an
  // incomplete filter.
  for (;;) {
    if (signal?.aborted) throw abortError();
    const page = await listDirectoryGroups(cursor ?? undefined, signal);
    if (signal?.aborted) throw abortError();
    groups.push(...page.items);
    if (!page.nextCursor) break;
    // A repeated cursor means the server is looping, so the walk did NOT reach
    // exhaustion — and what it collected is both truncated and duplicated. That
    // is a failure, not an answer: returning it would cache a filter missing
    // groups, exactly as a page cap would. Same reasoning as the abort checks.
    if (seen.has(page.nextCursor)) {
      throw new Error(
        "Directory group pagination repeated a cursor without reaching the end",
      );
    }
    seen.add(page.nextCursor);
    cursor = page.nextCursor;
  }
  return groups;
}

async function request(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<unknown> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...csrfHeaders(),
    },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  return parse(response);
}

async function parse(response: Response): Promise<unknown> {
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    const retryAfter = Number(response.headers.get("Retry-After"));
    throw new DirectoryApiError(
      body?.message ?? body?.code ?? `Directory request failed`,
      response.status,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
    );
  }
  return response.json();
}
